import axios from 'axios'
import { EVAL_STATE, EvalStateValue, ResponseStateValue } from './evaluationForm.constants'
import { QueryResult } from 'react-query'

export const EVAL_ROUTE_BASE = '/api/rest/recording/contact'
export const EVAL_FORM_ROUTE = '/api/rest/recording/evalform'

export const getEvaluationUrl = (contactId: number, evalId: number | null) => `${EVAL_ROUTE_BASE}/${contactId}/eval${evalId !== null ? `/${evalId}` as const : ''}` as const

export const getEvaluationCommentUrl = (contactId: number, evalId: number) => `${EVAL_ROUTE_BASE}/${contactId}/eval/${evalId}/comment` as const

export const getEvaluationFormUrl = (evalFormId: number) => `${EVAL_FORM_ROUTE}/${evalFormId}` as const

export const getCalibrationUrl = (contactId: number) => `${EVAL_ROUTE_BASE}/${contactId}/calibration` as const

export const getCalibrationEvaluation = (contactId: number) => `${EVAL_ROUTE_BASE}/${contactId}/eval?isScoreCounted=false&myEval=true` as const

export const getActiveEvalFormsUrl = `${EVAL_FORM_ROUTE}?status=1&exclude=sections`

export interface EvaluationFormSectionQuestion {
  id: number,
  selectedOption: $TSFIXME,
}

export interface EvaluationFormSection {
  id: number,
  questions: EvaluationFormSectionQuestion[],
}

export interface EvaluationFormValues {
  evaluatedTz: string,
  isScoreCounted: boolean,
  evalForm: { evalFormId: $TSFIXME },
  state?: { stateId: typeof EVAL_STATE[keyof typeof EVAL_STATE] },
  responseState: $TSFIXME,
  responseDate: $TSFIXME,
  id?: number | null,
  sections: EvaluationFormSection[],
}

export interface EvalState {
  stateId: number,
  text: string,
}

export interface EvalForm {
  $ref: string,
  name: string,
  evalFormId: number,
}

export interface EvalPerson {
  $ref: string,
  id: number,
  displayId: string,
  lastName: string,
  firstName: string,
  username: string,
}

export interface EvalQuestion {
  id: number,
  selectedOption: number,
  additiveScore: number,
  maxAdditiveScore: number,
}

export interface EvalSection {
  id: number,
  totalScore: number,
  additiveScore: number,
  maxAdditiveScore: number,
  hasKpi?: boolean,
  questions: EvalQuestion[],
}

export interface Eval {
  id: number,
  evaluated: number,
  evaluatedTz: string,
  state: EvalState,
  responseState: EvalState | null,
  responseDate: number,
  isScoreCounted: boolean,
  totalScore: number,
  additiveScore: number,
  qualityRef: string,
  comments?: string,
  evalForm: EvalForm,
  agent: EvalPerson | null,
  evaluator: EvalPerson | null,
  approver: EvalPerson | null,
  sections: EvalSection[],
}

export interface SaveEvalPersonPayload {
  id: number,
}

export interface SaveEvalQuestionPayload {
  id: number,
  selectedOption: number,
}

export interface SaveEvalSectionPayload {
  id: number,
  questions: SaveEvalQuestionPayload[],
}

export interface SaveEvalPayload {
  id?: number | null,
  evaluated: number,
  evaluatedTz?: string,
  state: { stateId: EvalStateValue },
  responseState?: { stateId: ResponseStateValue, reason: string } | null,
  responseDate: number,
  isScoreCounted: boolean,
  evalForm: { evalFormId: number },
  agent?: SaveEvalPersonPayload | null,
  evaluator?: SaveEvalPersonPayload | null,
  approver?: SaveEvalPersonPayload | null,
  sections: SaveEvalSectionPayload[],
}

function getSaveEvaluationPayload(values: EvaluationFormValues, isCalibration: boolean): SaveEvalPayload {
  return {
    evaluated: Date.now(),
    evaluatedTz: values.evaluatedTz,
    isScoreCounted: values.isScoreCounted || !isCalibration,
    evalForm: {
      evalFormId: values.evalForm.evalFormId,
    },
    state: {
      stateId:
        typeof values.state === 'undefined' || values.state.stateId === EVAL_STATE.UNSCORED ? 2 : values.state.stateId,
    },
    responseState: values.responseState || null,
    responseDate: values.responseDate || null,
    id: values.id || null,
    sections: values.sections.map(({ id, questions }) => ({
      id,
      questions: questions.map(({ id, selectedOption }) => ({
        id,
        selectedOption,
      })),
    })),
  }
}

export interface SaveEvaluationArgs {
  contactId: number,
  evalId: number,
  newValues: EvaluationFormValues,
  isCalibrationView?: boolean,
}

export async function saveEvaluation({ contactId, evalId, newValues, isCalibrationView = false }: SaveEvaluationArgs): Promise<Eval> {
  const payload = getSaveEvaluationPayload(newValues, isCalibrationView)
  const response = await axios.post<Eval>(getEvaluationUrl(contactId, evalId), payload)
  // API sends back empty string if going from NEEDS_CANNOT_SCORE_APPROVAL to IN_PROGRESS
  return response.data
}

export interface ResetEvaluationArgs {
  contactId: number,
  evalId?: number | null,
  isCalibrationView: boolean,
}

export async function resetEvaluation({ contactId, evalId }: ResetEvaluationArgs): Promise<Eval | undefined> {
  const response = evalId
    ? await axios.put<undefined>(getEvaluationUrl(contactId, evalId))
    : await axios.post<Eval>(getEvaluationUrl(contactId, null))
  return response.data
}

export interface GetEvaluationArgs {
  contactId: number,
  evalId: number,
}

export async function getEvaluation(_key: unknown, { contactId, evalId }: GetEvaluationArgs): Promise<Eval | null> {
  const response = await axios.get<Eval | Eval[]>(getEvaluationUrl(contactId, evalId))
  // API sends back empty array if no eval exists and object if it does
  return Array.isArray(response.data) ? null : response.data
}

export interface GetCommentsArgs {
  contactId: number,
  evalId: number,
}

export interface EvalCommentor {
  $ref?: string,
  displayId: string,
  lastName: string,
  firstName: string,
  username: string,
}

export interface EvalCommentHistory {
  created: number,
  tz: string,
  commentor: EvalCommentor,
}

export interface EvalComment {
  $ref: string,
  text: string,
  created: number,
  tz: string,
  contactOffset: number,
  questionFK?: number,
  sectionFK?: number,
  commentor: EvalCommentor,
  history: EvalCommentHistory[],
}

export async function getComments(_key: unknown, { contactId, evalId }: GetCommentsArgs): Promise<EvalComment[]> {
  const response = await axios.get<EvalComment[]>(getEvaluationCommentUrl(contactId, evalId))
  return response.data
}

export interface DraftComment {
  text: string,
  contactOffset: number,
  sectionFK: number | null,
  questionFK: number | null,
}

export interface AddCommentArgs {
  contactId: number,
  evalId: number,
  comment: DraftComment,
  isCalibrationView: boolean,
}

export async function addComment({ contactId, evalId, comment }: AddCommentArgs): Promise<EvalComment> {
  const { data } = await axios.post<EvalComment>(getEvaluationCommentUrl(contactId, evalId), comment)
  return data
}

export interface UpdateCommentArgs {
  comment: $TSFIXME,
  commentRef: string,
}

export async function updateComment({ comment, commentRef }: UpdateCommentArgs): Promise<EvalComment> {
  const response = await axios.put<EvalComment>(commentRef, comment)
  return response.data
}

export interface GetTemplateArgs {
  templateId: number,
}

export interface EvalFormBandRegion {
  band: number,
  begin: number,
  end: number,
}

export interface EvalFormScoreType {
  id: number,
  name: string,
}

export interface EvalFormResponseType {
  id: number,
  name: string,
}

export interface EvalFormQuestionOption {
  id: number,
  type: string,
  label: string,
  isDefault: boolean,
  kpiPriority: number,
  order: number,
  points: number,
  displaySection: number,
  displayQuestion: number,
}

export interface EvalFormQuestion {
  id: number,
  text: string,
  description: string,
  weight: number,
  options: EvalFormQuestionOption[],
  order: number,
  displayOnLoad: boolean,
}

export interface EvalFormSection {
  id: number,
  name: string,
  weight: number,
  startColor: string,
  endColor: string,
  questions: EvalFormQuestion[],
  order: number,
  displayOnLoad: boolean,
}

export interface EvalForm {
  id: number,
  name: string,
  description: string,
  creator: string,
  created: number,
  createdTz: string,
  manualWeighting: boolean,
  updater: string,
  updated: number,
  updatedTz: string,
  status: number,
  defaultForm: boolean,
  approvalRequired: boolean,
  bandRanges: EvalFormBandRegion[],
  scoreType: EvalFormScoreType,
  responseType: EvalFormResponseType,
  hasBranchingLogic: boolean,
  sections: EvalFormSection[],
}

export async function getTemplate(_key: unknown, { templateId }: GetTemplateArgs): Promise<EvalForm | null | undefined> {
  const response = await axios.get<EvalForm[]>(getEvaluationFormUrl(templateId))
  return response.data[0]
}

export async function getActiveEvalForms(): Promise<EvalForm[]> {
  try {
    const response = await axios.get<EvalForm[]>(getActiveEvalFormsUrl)
    return response.data
  } catch (e) {
    // @ts-expect-error TODO: remove try/catch; rethrowing an error wrapped in another error is not useful
    throw new Error(e)
  }
}

export interface GetCalibrationArgs {
  contactId: number,
}

interface CalibrationContactResp {
  id: number,
  calibrationForm: string,
  isCalibration: boolean,
  canCalibrate: boolean,
}

export interface CalibrationContact {
  id: number,
  calibrationForm: number,
  isCalibration: boolean,
  canCalibrate: boolean,
}

export async function getCalibration(_key: unknown, { contactId }: GetCalibrationArgs): Promise<CalibrationContact> {
  const { data } = await axios.get<CalibrationContactResp>(getCalibrationUrl(contactId))
  return { ...data, calibrationForm: parseInt(data.calibrationForm, 10) }
}

export interface GetCalibrationValuesArgs {
  contactId: number,
}

export async function getCalibrationValues(_key: unknown, { contactId }: GetCalibrationValuesArgs): Promise<Eval | null> {
  const { data } = await axios.get<Eval | []>(getCalibrationEvaluation(contactId))
  // API sends back empty array if no eval exists and object if it does
  return Array.isArray(data) ? null : data
}

export interface EvaluationData {
  template: QueryResult<EvalForm | null>,
  values: QueryResult<Eval | null>,
  comments: QueryResult<EvalComment[] | null>,
}
